/**
 * ConversionEngine - motor centralizado de conversão (track + autoTracks).
 * Espelha a arquitetura do BehaviorEngine: init, state, delegação para trackers.
 */

import type { GomarkePixelConfig } from '../types';
import type { PixelResponse } from '../types';
import { getCoreSessionId, getCoreVisitorId } from '../core';
import {
  enqueueEvent,
  sendPageViewImmediately,
  sendEventImmediately,
} from '../core/queue';
import { runEventPipeline } from '../core/pipeline';
import type { QueuePriority } from '../core/queue';
import { TrackerFactory } from './tracks/TrackerFactory';
import type { IPurchaseData } from './tracks/purchase';
import type { ILeadData } from './tracks/lead';
import type { IAddToCartData } from './tracks/addToCart';
import type { IInitiateCheckoutData } from './tracks/initiateCheckout';
import type { IAddPaymentInfoData } from './tracks/addPaymentInfo';
import type { IRemoveToCartData } from './tracks/removeToCart';
import type { ISearchData } from './tracks/search';
import type { IViewContentData } from './tracks/viewContent';

export type ConversionEventType =
  | 'pageView'
  | 'purchase'
  | 'lead'
  | 'addToCart'
  | 'initiateCheckout'
  | 'addPaymentInfo'
  | 'removeToCart'
  | 'search'
  | 'viewContent';

export type ConversionEventData =
  | void
  | IPurchaseData
  | ILeadData
  | IAddToCartData
  | IInitiateCheckoutData
  | IAddPaymentInfoData
  | IRemoveToCartData
  | ISearchData
  | IViewContentData;

export interface TrackConversionOptions {
  /** Chave para deduplicação; se não informada, é gerada a partir de eventType e data. */
  eventId?: string;
}

export type TrackConversionFn = (
  eventType: ConversionEventType,
  data?: ConversionEventData,
  options?: TrackConversionOptions
) => Promise<PixelResponse>;

function shouldSendImmediately(eventType: ConversionEventType): boolean {
  return (
    eventType === 'addPaymentInfo' ||
    eventType === 'initiateCheckout' ||
    eventType === 'purchase'
  );
}

function getPriorityForEventType(
  eventType: ConversionEventType
): QueuePriority {
  if (eventType === 'purchase' || eventType === 'lead') return 'high';
  if (
    eventType === 'addToCart' ||
    eventType === 'initiateCheckout' ||
    eventType === 'addPaymentInfo' ||
    eventType === 'removeToCart'
  )
    return 'medium';
  return 'low';
}

let engineConfig: {
  config: GomarkePixelConfig;
  version: string;
  sessionId: string;
  visitorId: string;
  trackerFactory: TrackerFactory;
  sentEventKeys: Set<string>;
} | null = null;

export interface InitConversionEngineOptions {
  config: GomarkePixelConfig;
  version: string;
  sessionId?: string;
  visitorId?: string;
}

/**
 * Inicializa o ConversionEngine (chamar após Core e Behavior).
 * Se sessionId/visitorId não forem passados, lê do Core.
 */
export function initConversionEngine(
  options: InitConversionEngineOptions
): void {
  const sessionId = options.sessionId ?? getCoreSessionId() ?? '';
  const visitorId = options.visitorId ?? getCoreVisitorId() ?? '';

  engineConfig = {
    config: options.config,
    version: options.version,
    sessionId,
    visitorId,
    trackerFactory: new TrackerFactory(
      options.config,
      sessionId,
      visitorId,
      options.version
    ),
    sentEventKeys: new Set(),
  };
}

/**
 * Gera chave de deduplicação quando eventId não é passado.
 */
function getEventKey(
  eventType: ConversionEventType,
  data?: ConversionEventData
): string {
  switch (eventType) {
    case 'purchase': {
      const d = (data ?? {}) as IPurchaseData;
      return d.transactionId
        ? `purchase_${d.transactionId}`
        : `purchase_${crypto.randomUUID()}`;
    }
    case 'lead': {
      const d = (data ?? {}) as ILeadData;
      return `lead_${d.email ?? ''}_${Date.now()}`;
    }
    case 'addToCart': {
      const d = (data ?? {}) as IAddToCartData;
      if (d.customer) {
        return `addToCart_customer_${engineConfig?.sessionId ?? 'session'}`;
      }
      return `addToCart_${d.itemId}_${d.itemName}_${Date.now()}`;
    }
    case 'initiateCheckout':
      return `initiateCheckout_${Date.now()}_${Math.random()}`;
    case 'addPaymentInfo':
      return `addPaymentInfo_${Date.now()}_${Math.random()}`;
    case 'removeToCart': {
      const d = (data ?? {}) as IRemoveToCartData;
      return `removeToCart_${d.itemId}_${d.itemName}_${Date.now()}`;
    }
    case 'search': {
      const d = (data ?? {}) as ISearchData;
      return `search_${d.searchTerm}_${Date.now()}`;
    }
    case 'viewContent':
      return `viewContent_${Date.now()}_${Math.random()}`;
    case 'pageView':
      return `pageView_${Date.now()}_${Math.random()}`;
    default:
      return `${eventType}_${Date.now()}_${Math.random()}`;
  }
}

/**
 * Verifica se um evento já foi enviado (deduplicação).
 */
export function hasConversionEvent(eventId: string): boolean {
  return engineConfig?.sentEventKeys.has(eventId) ?? false;
}

/**
 * Registra um evento como enviado (deduplicação).
 */
export function registerConversionEvent(eventId: string): void {
  engineConfig?.sentEventKeys.add(eventId);
}

/**
 * Atualiza sessionId no engine (ex.: renovação de sessão).
 */
export function updateConversionSessionId(sessionId: string): void {
  if (engineConfig) {
    engineConfig.sessionId = sessionId;
    engineConfig.trackerFactory.updateSessionId(sessionId);
  }
}

/**
 * Atualiza visitorId no engine.
 */
export function updateConversionVisitorId(visitorId: string): void {
  if (engineConfig) {
    engineConfig.visitorId = visitorId;
    engineConfig.trackerFactory.updateVisitorId(visitorId);
  }
}

/**
 * Envia um evento de conversão pelo tipo e dados.
 * Ponto único para track manual e para autoTrackers.
 * Deduplicação: se eventId (ou chave gerada) já foi enviado, retorna success sem reenviar.
 */
export function trackConversion(
  eventType: ConversionEventType,
  data?: ConversionEventData,
  options?: TrackConversionOptions
): Promise<PixelResponse> {
  if (!engineConfig) {
    return Promise.resolve({
      success: false,
      error: 'ConversionEngine não inicializado',
      timestamp: Date.now(),
    });
  }

  const eventKey = options?.eventId ?? getEventKey(eventType, data);
  if (engineConfig.sentEventKeys.has(eventKey)) {
    return Promise.resolve({
      success: true,
      event_id: `dedupe_${eventKey}`,
      timestamp: Date.now(),
    });
  }
  engineConfig.sentEventKeys.add(eventKey);

  const { trackerFactory } = engineConfig;

  try {
    let params;
    switch (eventType) {
      case 'pageView':
        params = trackerFactory.getPageViewTracker().getPayload();
        break;
      case 'purchase':
        params = trackerFactory
          .getPurchaseTracker()
          .getPayload((data ?? {}) as IPurchaseData);
        break;
      case 'lead':
        params = trackerFactory
          .getLeadTracker()
          .getPayload((data ?? {}) as ILeadData);
        break;
      case 'addToCart':
        params = trackerFactory
          .getAddToCartTracker()
          .getPayload((data ?? {}) as IAddToCartData);
        break;
      case 'initiateCheckout':
        params = trackerFactory
          .getInitiateCheckoutTracker()
          .getPayload((data ?? {}) as IInitiateCheckoutData);
        break;
      case 'addPaymentInfo':
        params = trackerFactory
          .getAddPaymentInfoTracker()
          .getPayload((data ?? {}) as IAddPaymentInfoData);
        break;
      case 'removeToCart':
        params = trackerFactory
          .getRemoveToCartTracker()
          .getPayload((data ?? {}) as IRemoveToCartData);
        break;
      case 'search':
        params = trackerFactory
          .getSearchTracker()
          .getPayload((data ?? {}) as ISearchData);
        break;
      case 'viewContent':
        params = trackerFactory
          .getViewContentTracker()
          .getPayload((data ?? {}) as IViewContentData);
        break;
      default:
        return Promise.resolve({
          success: false,
          error: `Tipo de evento desconhecido: ${String(eventType)}`,
          timestamp: Date.now(),
        });
    }
    if (eventType === 'pageView') {
      return sendPageViewImmediately(params);
    }
    const metadata =
      data && typeof data === 'object' && 'metadata' in data
        ? (data as { metadata?: Record<string, unknown> }).metadata
        : undefined;
    const piped = runEventPipeline({ params, metadata });
    if (!piped.params) {
      engineConfig.sentEventKeys.delete(eventKey);
      return Promise.resolve({
        success: false,
        error: piped.error ?? 'validation_failed',
        timestamp: Date.now(),
      });
    }
    if (shouldSendImmediately(eventType)) {
      return sendEventImmediately(piped.params);
    }
    const priority = getPriorityForEventType(eventType);
    enqueueEvent(piped.params, priority);
    return Promise.resolve({
      success: true,
      event_id: `queued_${eventKey}`,
      timestamp: Date.now(),
    });
  } catch (err) {
    engineConfig.sentEventKeys.delete(eventKey);
    return Promise.resolve({
      success: false,
      error: err instanceof Error ? err.message : 'Erro ao montar payload',
      timestamp: Date.now(),
    });
  }
}

/**
 * Retorna a função de track para injetar em AutoTrackerFactory / autoTrackers.
 */
export function getConversionTrack(): TrackConversionFn {
  return trackConversion;
}

/**
 * Retorna o TrackerFactory interno (para compatibilidade/getTrackerFactory no pixel).
 */
export function getConversionTrackerFactory(): TrackerFactory | null {
  return engineConfig?.trackerFactory ?? null;
}

/**
 * Atualiza a configuração do engine.
 */
export function updateConversionConfig(
  newConfig: Partial<GomarkePixelConfig>
): void {
  if (engineConfig) {
    engineConfig.config = { ...engineConfig.config, ...newConfig };
    engineConfig.trackerFactory.updateConfig(newConfig);
  }
}

/**
 * Destrói o engine (limpa estado e set de deduplicação).
 */
export function destroyConversionEngine(): void {
  if (engineConfig) {
    engineConfig.trackerFactory.clearTrackers();
    engineConfig.sentEventKeys.clear();
    engineConfig = null;
  }
}
